import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { isPasteTargetArmed } from '../components/media-capture-button/paste-arm';
import { AppConstants } from '../app-constants';
import { clearSharedFiles, loadSharedFiles, saveSharedFiles } from './shared-files-store';

/**
 * Files the user brought in from outside (paste anywhere, later: Android/iPhone "Share to LumberLinq")
 * and has not attached to a screen yet. SHARE_TO_LUMBERLINQ_PLAN.md.
 *
 * This service never uploads anything itself. It only HOLDS the files and remembers where the user said
 * they belong; the destination screen then feeds them into its OWN existing upload code (through
 * `<app-shared-files-banner>` or an explicit take()), so that screen's rights, plan/storage limits,
 * locked states, file-type checks and progress line all apply exactly as for a normal upload.
 */
export type ShareTarget = 'CONSIGNMENT' | 'TALLY_DOCS' | 'AI_IMPORT' | 'TICKET';

/** Consignment document slot — the value is the same `category` string the shipment screen already uses. */
export type ConsignmentDocType =
  'BL' | 'Invoice' | 'E-Waybill' | 'Certificate of Origin' | 'Phytosanitary' | 'Other';

export interface PendingShare {
  files: File[];
  target: ShareTarget | null;
  docType?: ConsignmentDocType;
}

/** Same ceiling the shipment screen uses per document type; keeps a runaway paste from filling memory. */
export const SHARED_FILES_MAX = 20;

/** Waiting files older than this are dropped when restored (same 12 h the phone keeps un-collected shares). */
const SHARED_FILES_MAX_AGE_MS = 12 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class SharedFilesService {
  private readonly pendingSubject = new BehaviorSubject<PendingShare | null>(null);
  private readonly sheetOpenSubject = new BehaviorSubject<boolean>(false);
  /** How many sheet hosts are mounted (the host lives in the logged-in shell only). 0 = ignore pastes. */
  private hostCount = 0;
  /** True while the disk copy is being read at start-up; a paste in that instant is ignored (a few ms). */
  private restoring = false;

  readonly pending$ = this.pendingSubject.asObservable();
  readonly sheetOpen$ = this.sheetOpenSubject.asObservable();

  constructor(private zone: NgZone) {
    if (typeof document !== 'undefined') {
      document.addEventListener('paste', (e) => this.onGlobalPaste(e as ClipboardEvent));
    }
  }

  get pending(): PendingShare | null { return this.pendingSubject.value; }

  /**
   * Called by the sheet host when it mounts/unmounts (unmount = logout: nothing may leak to the next user).
   * The first host to mount also restores files that were waiting when the page was reloaded / the app restarted;
   * await it before collecting new native shares so the two are merged, not overwritten.
   */
  async registerHost(): Promise<void> {
    this.hostCount++;
    if (this.hostCount === 1) {
      this.restoring = true;
      try { await this.loadPersisted(); } finally { this.restoring = false; }
    }
  }
  unregisterHost(): void {
    this.hostCount = Math.max(0, this.hostCount - 1);
    if (this.hostCount === 0) this.discard();
  }

  /** Put files on hold and open the "Where should these go?" sheet. Adds to files already waiting. */
  offer(files: File[]): void {
    if (!files.length) return;
    const existing = this.pending?.files ?? [];
    const merged = [...existing];
    for (const f of files) {
      if (!merged.some(m => m.name === f.name && m.size === f.size && m.lastModified === f.lastModified)) merged.push(f);
    }
    this.setPending({ files: merged.slice(0, SHARED_FILES_MAX), target: null });
    this.sheetOpenSubject.next(true);
  }

  closeSheet(): void { this.sheetOpenSubject.next(false); }

  removeFile(index: number): void {
    const p = this.pending;
    if (!p) return;
    const files = p.files.filter((_, i) => i !== index);
    if (!files.length) { this.discard(); return; }
    this.setPending({ ...p, files });
  }

  /** The user picked a place. Files stay on hold until that screen takes them. */
  choose(target: ShareTarget, docType?: ConsignmentDocType): void {
    const p = this.pending;
    if (!p) return;
    this.setPending({ ...p, target, docType });
    this.sheetOpenSubject.next(false);
  }

  // A screen that is ALREADY open can take files for its own kind right where the user is (an open Stock Unit
  // brings its Photos tab forward) — picking that place then does not have to jump to a list first.
  private readonly openers = new Map<ShareTarget, () => boolean>();

  /** The open screen says "I can take these now". Returns a function that removes it again (call on destroy). */
  registerOpener(target: ShareTarget, open: () => boolean): () => void {
    this.openers.set(target, open);
    return () => { if (this.openers.get(target) === open) this.openers.delete(target); };
  }

  /** True when the open screen took over (files stay on hold until its Attach button is pressed); false = go to the list. */
  openHere(target: ShareTarget): boolean {
    try { return this.openers.get(target)?.() === true; } catch { return false; }
  }

  /** Takes back a choose() (the sheet goes on to a second step, e.g. the record picker, before the place is final). */
  undoChoose(): void {
    const p = this.pending;
    if (p) this.setPending({ ...p, target: null, docType: undefined });
  }

  reopenSheet(): void { this.sheetOpenSubject.next(true); }

  /** The destination screen claims its files; they are removed from the hold. Null if none are waiting for it. */
  take(target: ShareTarget): PendingShare | null {
    const p = this.pending;
    if (!p || p.target !== target) return null;
    this.setPending(null);
    return p;
  }

  /** Put files back (used when a screen took them but could not use them after all). */
  restore(p: PendingShare): void { this.setPending(p); }

  discard(): void {
    this.setPending(null);
    this.sheetOpenSubject.next(false);
  }

  // ── Disk copy (survives a reload) ─────────────────────────────────────────────────────────────────────

  /** Serialises disk writes so a quick change followed by a discard can never leave a stale copy behind. */
  private saveChain: Promise<void> = Promise.resolve();

  private setPending(p: PendingShare | null): void {
    this.pendingSubject.next(p);
    const owner = this.owner();
    this.saveChain = this.saveChain.then(() =>
      p ? saveSharedFiles(owner, p.target, p.docType, p.files) : clearSharedFiles());
  }

  /** Who these files belong to. AuthSessionGuard records the signed-in user + company here before the shell shows. */
  private owner(): string {
    try {
      return `${localStorage.getItem(AppConstants.LAST_DEVICE_USER_EMAIL)}|${localStorage.getItem(AppConstants.LAST_DEVICE_TENANT_ID)}`;
    } catch {
      return 'unknown';
    }
  }

  /** Bring back waiting files after a reload — only for the same user + company, only while fresh. */
  private async loadPersisted(): Promise<void> {
    const row = await loadSharedFiles();
    if (!row) return;
    const usable = row.files.length
      && row.owner === this.owner()
      && Date.now() - row.savedAt < SHARED_FILES_MAX_AGE_MS;
    if (!usable) { await clearSharedFiles(); return; }

    const restored = row.files;
    this.zone.run(() => {
      // Anything offered while we were reading the disk (a paste, a native share) is kept, not overwritten.
      const current = this.pending?.files ?? [];
      const merged = [...restored];
      for (const f of current) {
        if (!merged.some(m => m.name === f.name && m.size === f.size && m.lastModified === f.lastModified)) merged.push(f);
      }
      this.setPending({
        files: merged.slice(0, SHARED_FILES_MAX),
        target: this.pending?.target ?? row.target,
        docType: this.pending?.docType ?? row.docType,
      });
      // Still waiting for the user to choose a place -> show the sheet again.
      if (!this.pending!.target) this.sheetOpenSubject.next(true);
    });
  }

  /**
   * Ctrl+V / Cmd+V with FILES on the clipboard and no upload button armed: open the sheet. Never steps in
   * for text, while typing in a field, while another dialog is open, or when logged out (no host mounted).
   */
  private onGlobalPaste(e: ClipboardEvent): void {
    if (this.hostCount === 0 || this.restoring) return;
    const files = Array.from(e.clipboardData?.files ?? []);
    if (!files.length) return;
    if (isPasteTargetArmed()) return; // that button handles it itself
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    if (this.sheetOpenSubject.value === false && document.querySelector('.p-dialog-mask, .p-drawer-mask')) return;
    e.preventDefault();
    this.zone.run(() => this.offer(files));
  }
}
